import { useEffect, useState } from 'react'
import { AlertCircle, Check, Plus, Save, Trash2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Checkbox } from './ui/checkbox'
import { Switch } from './ui/switch'

type UserRole = 'owner' | 'admin' | 'member' | 'manager'
type Channel = 'slack' | 'email' | 'sms'
type AlertType = 'approaching_budget' | 'budget_exceeded' | 'usage_spike'

interface BudgetRow { pod_id: string; name: string; monthly_limit_usd: string; spend_usd: number | null }
interface AlertRule { id?: string; percentage: string; alert_type: AlertType; channels: Channel[]; enabled: boolean }

interface BudgetConfigurationProps { teamId: string; userRole: UserRole }

const alertTypeLabels: Record<AlertType, string> = {
  approaching_budget: 'Approaching budget',
  budget_exceeded: 'Budget exceeded',
  usage_spike: 'Usage spike',
}

export function BudgetConfiguration({ teamId, userRole }: BudgetConfigurationProps) {
  const [budgets, setBudgets] = useState<BudgetRow[]>([])
  const [rules, setRules] = useState<AlertRule[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const isAdmin = userRole === 'admin' || userRole === 'owner'
  const canEditBudgets = isAdmin

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const [{ data: pods, error: podsError }, { data: loadedRules, error: rulesError }] = await Promise.all([
          (supabase as any).from('pods').select('id, name').eq('team_id', teamId).is('archived_at', null).order('name'),
          (supabase as any).from('budget_alert_rules').select('id, percentage, alert_type, channels, enabled').eq('team_id', teamId).order('percentage'),
        ])
        if (podsError) throw podsError
        if (rulesError && rulesError.code !== 'PGRST116') throw rulesError
        const podRows = (pods ?? []) as { id: string; name: string }[]
        const { data: podLimits, error: podLimitError } = podRows.length
          ? await (supabase as any).from('budget_thresholds').select('pod_id, monthly_limit_usd').in('pod_id', podRows.map((pod) => pod.id))
          : { data: [], error: null }
        if (podLimitError) throw podLimitError
        if (cancelled) return
        const limitByPod = new Map((podLimits ?? []).map((row: any) => [row.pod_id, row.monthly_limit_usd]))
        const today = new Date()
        const todayKey = today.toISOString().slice(0, 10)
        const monthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1)).toISOString().slice(0, 10)
        // The aggregate RPC is intentionally service-role-only because it is
        // also used by the alert worker. The admin UI reads the same frozen
        // cost_usd values through the normal RLS-protected event relation.
        const { data: spendRows, error: spendError } = podRows.length
          ? await (supabase as any)
            .from('agent_usage_events')
            .select('cost_usd, agent:agents!agent_usage_events_agent_id_fkey(user_id, user:users!agents_user_id_fkey(pod_id))')
            .eq('team_id', teamId)
            .gte('usage_date', monthStart)
            .lte('usage_date', todayKey)
            .not('cost_usd', 'is', null)
          : { data: [], error: null }
        if (spendError) throw spendError
        const spendByPod = new Map<string, number>()
        for (const row of spendRows ?? []) {
          const agent = Array.isArray(row.agent) ? row.agent[0] : row.agent
          const owner = agent?.user && Array.isArray(agent.user) ? agent.user[0] : agent?.user
          if (owner?.pod_id) spendByPod.set(owner.pod_id, (spendByPod.get(owner.pod_id) ?? 0) + Number(row.cost_usd ?? 0))
        }
        setBudgets(podRows.map((pod) => ({ pod_id: pod.id, name: pod.name, monthly_limit_usd: limitByPod.get(pod.id) == null ? '' : String(limitByPod.get(pod.id)), spend_usd: spendByPod.get(pod.id) ?? 0 })))
        setRules((loadedRules ?? []).map((rule: any) => ({ id: rule.id, percentage: String(rule.percentage), alert_type: rule.alert_type, channels: (rule.channels ?? ['slack']).filter((channel: string): channel is Channel => channel === 'slack' || channel === 'email' || channel === 'sms'), enabled: rule.enabled })))
      } catch (loadError: any) {
        if (!cancelled) setError(loadError.message || 'Failed to load budget settings')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [teamId])

  const updateBudget = (podId: string, value: string) => {
    setBudgets((current) => current.map((budget) => budget.pod_id === podId ? { ...budget, monthly_limit_usd: value } : budget))
    setSuccess('')
  }

  const updateRule = (index: number, patch: Partial<AlertRule>) => {
    setRules((current) => current.map((rule, ruleIndex) => ruleIndex === index ? { ...rule, ...patch } : rule))
    setSuccess('')
  }

  const addRule = () => {
    if (!isAdmin) return
    setRules((current) => [...current, { percentage: '80', alert_type: 'approaching_budget', channels: ['slack'], enabled: true }])
  }

  const save = async () => {
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      if (canEditBudgets) {
        for (const budget of budgets) {
          if (!budget.monthly_limit_usd) {
            await (supabase as any).from('budget_thresholds').delete().eq('pod_id', budget.pod_id)
            continue
          }
          const amount = Number(budget.monthly_limit_usd)
          if (!Number.isFinite(amount) || amount <= 0) throw new Error(`Enter a valid budget for ${budget.name}`)
          const { error: budgetError } = await (supabase as any).from('budget_thresholds').upsert({ pod_id: budget.pod_id, monthly_limit_usd: amount, updated_by: (await supabase.auth.getUser()).data.user?.id }, { onConflict: 'pod_id' })
          if (budgetError) throw budgetError
        }
      }
      if (isAdmin) {
        const { data: existing } = await (supabase as any).from('budget_alert_rules').select('id').eq('team_id', teamId)
        const currentIds = new Set(rules.map((rule) => rule.id).filter(Boolean))
        for (const existingRule of existing ?? []) if (!currentIds.has(existingRule.id)) await (supabase as any).from('budget_alert_rules').delete().eq('id', existingRule.id).eq('team_id', teamId)
        for (const rule of rules) {
          const percentage = Number(rule.percentage)
          if (!Number.isFinite(percentage) || percentage <= 0 || percentage > 1000) throw new Error('Alert percentages must be between 1 and 1000')
          if (!rule.channels.length) throw new Error('Each alert threshold needs at least one channel')
          const { error: ruleError } = await (supabase as any).from('budget_alert_rules').upsert({ id: rule.id, team_id: teamId, percentage, alert_type: rule.alert_type, channels: rule.channels, enabled: rule.enabled }, { onConflict: 'team_id,percentage,alert_type' })
          if (ruleError) throw ruleError
        }
      }
      setSuccess('Budget settings saved successfully.')
    } catch (saveError: any) {
      setError(saveError.message || 'Failed to save budget settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading budget settings…</div>

  return (
    <div className="bg-background">
      <div className="border-b border-border p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-foreground">Spend budgets</h2>
        <p className="mt-1 text-sm text-muted-foreground">Set a monthly connector-spend budget per team, and get alerted before it&apos;s exceeded.</p>
      </div>
      {error && <div role="alert" className="flex items-center gap-2 border-b border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"><AlertCircle className="h-4 w-4" />{error}</div>}
      {success && <div role="status" className="flex items-center gap-2 border-b border-primary/30 bg-primary/10 px-4 py-3 text-sm"><Check className="h-4 w-4" />{success}</div>}
      <div className="grid grid-cols-1 divide-y divide-border lg:grid-cols-2 lg:divide-x lg:divide-y-0">
        <section className="p-4 sm:p-6">
          <h3 className="text-sm font-semibold text-foreground">Monthly budget per team</h3>
          <p className="mt-1 border-b border-border pb-4 text-xs text-muted-foreground">Leave a team blank to skip budgeting for it.</p>
          <div className="divide-y divide-border">
            {budgets.map((budget) => {
              const limit = Number(budget.monthly_limit_usd)
              const spend = budget.spend_usd ?? 0
              const hasBudget = Number.isFinite(limit) && limit > 0
              const percent = hasBudget ? (spend / limit) * 100 : 0
              const overBudget = hasBudget && spend > limit
              const atLimit = hasBudget && spend >= limit
              const visualMaxPercent = overBudget ? Math.max(125, Math.ceil(percent / 25) * 25) : 100
              const budgetMarkerWidth = Math.min((100 / visualMaxPercent) * 100, 100)
              const usageWidth = Math.min((percent / visualMaxPercent) * 100, 100)
              return (
                <div key={budget.pod_id} className="space-y-3 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="truncate text-sm font-medium">{budget.name}</span>
                    <div className="flex w-32 shrink-0 items-center gap-1"><span className="text-sm text-muted-foreground">$</span><Input aria-label={`${budget.name} monthly budget`} value={budget.monthly_limit_usd} onChange={(event) => updateBudget(budget.pod_id, event.target.value)} disabled={!canEditBudgets} placeholder="No budget" inputMode="decimal" className="h-9 text-right" /></div>
                  </div>
                  {hasBudget ? (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs text-muted-foreground"><span>${spend.toFixed(2)} spent of ${limit.toFixed(2)}</span><span className={atLimit ? 'font-medium text-destructive' : 'font-medium text-emerald-600'}>{overBudget ? `$${(spend - limit).toFixed(2)} over` : atLimit ? 'At limit' : `$${(limit - spend).toFixed(2)} remaining`}</span></div>
                      <div className="relative h-2.5 overflow-hidden rounded-full border border-border bg-slate-300/70 dark:bg-slate-700/70" role="progressbar" aria-label={`${budget.name} budget usage`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(percent)}>
                        {!overBudget && <div className={`h-full rounded-full transition-all ${atLimit ? 'bg-destructive' : 'bg-emerald-500'}`} style={{ width: `${usageWidth}%` }} />}
                        {overBudget && (
                          <>
                            <div className="absolute inset-y-0 left-0 rounded-l-full bg-emerald-500 transition-all" style={{ width: `${budgetMarkerWidth}%` }} />
                            <div className="absolute inset-y-0 rounded-r-full bg-destructive transition-all" style={{ left: `${budgetMarkerWidth}%`, width: `${Math.max(usageWidth - budgetMarkerWidth, 0)}%` }} />
                            <span className="absolute inset-y-[-2px] w-0.5 -translate-x-1/2 bg-foreground/70" style={{ left: `${budgetMarkerWidth}%` }} aria-label="Budget limit" />
                          </>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground">{Math.round(percent)}% of this month&apos;s budget used</p>
                    </div>
                  ) : <p className="text-xs text-muted-foreground">${spend.toFixed(2)} spent this month · Set a budget to track remaining spend.</p>}
                </div>
              )
            })}
            {budgets.length === 0 && <p className="py-8 text-sm text-muted-foreground">No active teams found.</p>}
          </div>
        </section>
        <section className="p-4 sm:p-6">
          <div className="flex items-start justify-between gap-3"><div><h3 className="text-sm font-semibold text-foreground">Alert thresholds</h3><p className="mt-1 border-b border-border pb-4 text-xs text-muted-foreground">Applies to every team&apos;s budget. Choose where each alert is sent.</p></div></div>
          <div className="divide-y divide-border">
            {rules.map((rule, index) => <div key={rule.id ?? `new-${index}`} className="space-y-3 py-4"><div className="flex items-center gap-2"><Input aria-label="Alert percentage" value={rule.percentage} onChange={(event) => updateRule(index, { percentage: event.target.value })} disabled={!isAdmin} className="h-9 w-20" /><span className="text-xs text-muted-foreground">%</span><Select value={rule.alert_type} onValueChange={(value) => updateRule(index, { alert_type: value as AlertType })} disabled={!isAdmin}><SelectTrigger aria-label="Alert type" className="h-9 flex-1"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(alertTypeLabels).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent></Select><Switch checked={rule.enabled} onCheckedChange={(enabled) => updateRule(index, { enabled })} disabled={!isAdmin} aria-label="Enable alert" /><Button variant="ghost" size="icon" onClick={() => setRules((current) => current.filter((_, ruleIndex) => ruleIndex !== index))} disabled={!isAdmin} aria-label="Remove alert threshold"><Trash2 className="h-4 w-4 text-muted-foreground" /></Button></div><div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground"><span>Send via</span>{(['slack', 'sms', 'email'] as Channel[]).map((channel) => { const checked = rule.channels.includes(channel); const label = channel === 'slack' ? 'Slack' : channel === 'email' ? 'Email' : 'SMS'; return <label key={channel} className="flex items-center gap-2"><Checkbox checked={checked} onCheckedChange={(value) => { if (value) updateRule(index, { channels: [...rule.channels, channel] }); else if (rule.channels.length > 1) updateRule(index, { channels: rule.channels.filter((current) => current !== channel) }) }} disabled={!isAdmin || (checked && rule.channels.length === 1)} aria-label={`Send ${label} alerts`} /><span>{label}</span></label> })}</div></div>)}
          </div>
          <Button type="button" variant="outline" onClick={addRule} disabled={!isAdmin} className="mt-4 border-dashed text-primary"><Plus className="mr-2 h-4 w-4" />Add threshold</Button>
        </section>
      </div>
      <div className="flex justify-end border-t border-border p-4 sm:p-6"><Button onClick={() => void save()} disabled={saving || (!isAdmin && !canEditBudgets)}><Save className="mr-2 h-4 w-4" />{saving ? 'Saving…' : 'Save changes'}</Button></div>
    </div>
  )
}
